import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Send, ThumbsDown, ThumbsUp, UserRoundCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, streamPost } from "@/lib/api";
import { QuestionAllowance, useUsage } from "@/components/UsageMeter";
import { ProNudge } from "@/components/ProNudge";
import { FollowUps } from "@/components/FollowUps";
import { PlaybookProvider, SaveButton } from "@/components/SaveToPlaybook";
import { useAuth } from "@/context/AuthContext";
import { StarterQuestions } from "@/components/StarterQuestions";

const ASK_PLACEHOLDER = {
  cpg: "e.g. How do I approach a buyer at an independent deli for the first time?",
  hospitality: "e.g. Tuesdays are dead. How do I get 20 more covers without discounting?",
  both: "e.g. How do I grow my audience without spending a lot on ads?",
};
import { AIBadge, LindsayReply, Markdown, PageHeader, StatusBadge, fmtDate } from "@/components/Bits";

const QuestionCard = ({ q, onChange, onAskNew, usage, reloadUsage }) => {
  const escalate = async () => {
    try {
      const { data } = await api.post(`/questions/${q.id}/escalate`, { note: "" });
      onChange(data);
      toast.success("Sent to Lindsay. She'll reply personally.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const rate = async (helpful) => {
    await api.post(`/questions/${q.id}/rate`, { helpful }).catch(() => {});
    onChange({ ...q, helpful });
  };
  return (
    <article className="rounded-2xl border border-border bg-card p-6 lg:p-8 fade-up" data-testid={`question-card-${q.id}`}>
      <div className="flex items-center justify-between gap-4 mb-4">
        <span className="text-xs font-mono text-muted-foreground">{fmtDate(q.created_at)}</span>
        <StatusBadge status={q.status} testId={`question-status-${q.id}`} />
      </div>
      <h3 className="text-lg font-semibold mb-5">{q.question}</h3>
      {q.ai_answer && <><AIBadge /><div className="mt-4"><Markdown testId={`question-answer-${q.id}`}>{q.ai_answer}</Markdown></div></>}
      {q.status === "in_review" && (
        <p className="mt-2 text-sm text-sky-700" data-testid={`question-in-review-note-${q.id}`}>
          Lindsay is reviewing your answer before it's released. You'll see it here once she's checked it.
        </p>
      )}
      {q.status === "escalated" && (
        <p className="mt-4 text-sm text-rose-600" data-testid={`question-escalated-note-${q.id}`}>
          This one's gone to Lindsay for a personal answer. You'll see her reply here.
        </p>
      )}
      {q.lindsay_answer && <LindsayReply text={q.lindsay_answer} />}
      {q.status === "answered" && !q.out_of_scope && q.ai_answer && <FollowUps q={q} onChange={onChange} onAskNew={onAskNew} usage={usage} reloadUsage={reloadUsage} />}
      {q.status === "answered" && (
        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border pt-5">
          <span className="text-xs text-muted-foreground mr-1">Was this helpful?</span>
          <Button size="sm" variant="outline" onClick={() => rate(true)} data-testid={`question-helpful-${q.id}`}
            className={`rounded-full ${q.helpful === true ? "border-emerald-500 text-emerald-700" : ""}`}><ThumbsUp className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant="outline" onClick={() => rate(false)} data-testid={`question-not-helpful-${q.id}`}
            className={`rounded-full ${q.helpful === false ? "border-rose-500 text-rose-600" : ""}`}><ThumbsDown className="h-3.5 w-3.5" /></Button>
          {!q.out_of_scope && <SaveButton qid={q.id} testId={`question-save-${q.id}`} />}
          <Button size="sm" variant="ghost" onClick={escalate} data-testid={`question-escalate-${q.id}`} className="rounded-full text-pinkb ml-auto">
            <UserRoundCheck className="h-4 w-4 mr-1.5" />Ask Lindsay personally
          </Button>
        </div>
      )}
    </article>
  );
};

export default function Ask() {
  const { user } = useAuth();
  const [usage, reloadUsage] = useUsage();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [live, setLive] = useState(null);
  const formRef = useRef(null);
  const askNew = (question) => {
    setText(question);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  useEffect(() => { api.get("/questions").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const q = sp.get("q");
    if (!q) return;
    setText(q);
    setSp({}, { replace: true });
    setTimeout(() => formRef.current?.querySelector("textarea")?.focus(), 100);
  }, [sp, setSp]);

  const update = (q) => setItems((list) => list.map((x) => (x.id === q.id ? q : x)));

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 5) return toast.error("Tell me a little more first.");
    const question = text.trim();
    setLive({ question, answer: "" });
    setText("");
    try {
      await streamPost("/questions", { question }, (ev) => {
        if (ev.type === "delta") setLive((l) => ({ ...l, answer: l.answer + ev.content }));
        if (ev.type === "done") { setItems((list) => [ev.record, ...list]); setLive(null); }
        if (ev.type === "error") { toast.error(ev.message); setLive(null); api.get("/questions").then((r) => setItems(r.data)); }
      });
    } catch (err) {
      toast.error(err.message);
      setText(question);
      setLive(null);
    }
    reloadUsage();
  };

  const limitReached = usage && !!usage.questions_limit && usage.questions_used >= usage.questions_limit;

  return (
    <PlaybookProvider>
    <div className="max-w-4xl">
      <PageHeader eyebrow="Ask Lindsay" title="What's on your mind?" />
      <form ref={formRef} onSubmit={submit} className="rounded-2xl border border-border bg-card p-5 mb-4" data-testid="ask-form">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} disabled={!!live || limitReached} data-testid="ask-input"
          placeholder={ASK_PLACEHOLDER[user?.audience] || ASK_PLACEHOLDER.both}
          className="bg-transparent border-0 focus-visible:ring-0 text-base resize-none p-1" />
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
          <AIBadge />
          <Button type="submit" disabled={!!live || limitReached} data-testid="ask-submit-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white">
            {live ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Ask
          </Button>
        </div>
      </form>
      {!live && items.length < 3 && !limitReached && <StarterQuestions audience={user?.audience} onPick={(q) => { setText(q); formRef.current?.querySelector("textarea")?.focus(); }} />}
      <ProNudge usage={usage} />
      <QuestionAllowance usage={usage} limitReached={limitReached} />
      <p className="text-xs text-muted-foreground mb-10" data-testid="ask-privacy-note">Ask anything about your food, drink or hospitality business. If your question is covered in the knowledge base, you'll get an instant answer. If it's relevant to the industry but not yet in the knowledge base, your question may be passed to Lindsay to add to a future update. If your question is outside the scope of food, drink and hospitality marketing, you'll receive a message letting you know. Your questions are private.</p>
      <div className="space-y-6">
        {live && (
          <article className="rounded-2xl border border-pinkb/50 bg-card p-6 lg:p-8" data-testid="ask-live-answer">
            <h3 className="text-lg font-semibold mb-5">{live.question}</h3>
            <AIBadge />
            <div className="mt-4">{live.answer ? <Markdown>{live.answer}</Markdown> : <p className="text-sm text-muted-foreground animate-pulse">Checking the knowledge bank…</p>}</div>
          </article>
        )}
        {items.map((q) => <QuestionCard key={q.id} q={q} onChange={update} onAskNew={askNew} usage={usage} reloadUsage={reloadUsage} />)}
        {!live && items.length === 0 && <p className="text-muted-foreground text-sm" data-testid="ask-empty">No questions yet. Ask your first one above.</p>}
      </div>
    </div>
    </PlaybookProvider>
  );
}
